// Phase 13.6 — embed detection for lesson video URLs
//
// detectVideoEmbed(url) -> { provider, embed_url, id }
//   provider: "youtube" | "vimeo" | "file" | null
//   embed_url: iframe src for youtube/vimeo, original src for "file"
//   id: youtube/vimeo id (or null for file)
//
// Supported inputs:
//   YouTube:
//     https://www.youtube.com/watch?v=ID
//     https://youtu.be/ID
//     https://www.youtube.com/embed/ID
//     https://www.youtube.com/shorts/ID
//     https://www.youtube.com/live/ID
//     https://m.youtube.com/watch?v=ID
//   Vimeo:
//     https://vimeo.com/ID
//     https://vimeo.com/channels/.../ID
//     https://player.vimeo.com/video/ID
//   File: any URL ending in .mp4|.webm|.mov|.m4v|.ogg
//
// Returns { provider:null, embed_url:null, id:null } when unrecognised.

const YT_HOSTS = new Set([
  "youtube.com", "www.youtube.com", "m.youtube.com",
  "youtu.be", "www.youtu.be", "youtube-nocookie.com", "www.youtube-nocookie.com",
]);

const VIMEO_HOSTS = new Set([
  "vimeo.com", "www.vimeo.com", "player.vimeo.com",
]);

const FILE_RE = /\.(mp4|webm|mov|m4v|ogg)(\?|#|$)/i;

export function detectVideoEmbed(rawUrl) {
  const empty = { provider: null, embed_url: null, id: null };
  if (!rawUrl || typeof rawUrl !== "string") return empty;
  const url = rawUrl.trim();
  if (!url) return empty;

  let u;
  try {
    u = new URL(url);
  } catch {
    return empty;
  }

  const host = u.hostname.toLowerCase();

  // YouTube
  if (YT_HOSTS.has(host)) {
    let id = null;
    if (host === "youtu.be" || host === "www.youtu.be") {
      id = u.pathname.replace(/^\//, "").split("/")[0] || null;
    } else if (u.pathname.startsWith("/watch")) {
      id = u.searchParams.get("v");
    } else {
      // /embed/ID, /shorts/ID, /live/ID, /v/ID
      const m = u.pathname.match(/^\/(embed|shorts|live|v)\/([^/?#]+)/);
      if (m) id = m[2];
    }
    if (id) {
      return {
        provider: "youtube",
        embed_url: `https://www.youtube.com/embed/${id}`,
        id,
      };
    }
    return empty;
  }

  // Vimeo
  if (VIMEO_HOSTS.has(host)) {
    let id = null;
    if (host === "player.vimeo.com") {
      const m = u.pathname.match(/^\/video\/(\d+)/);
      if (m) id = m[1];
    } else {
      // path may be /<id> or /channels/foo/<id> or /album/x/video/<id>
      const parts = u.pathname.split("/").filter(Boolean);
      // last numeric segment
      for (let i = parts.length - 1; i >= 0; i--) {
        if (/^\d+$/.test(parts[i])) { id = parts[i]; break; }
      }
    }
    if (id) {
      return {
        provider: "vimeo",
        embed_url: `https://player.vimeo.com/video/${id}`,
        id,
      };
    }
    return empty;
  }

  // Plain file
  if (FILE_RE.test(u.pathname) || FILE_RE.test(url)) {
    return { provider: "file", embed_url: url, id: null };
  }

  return empty;
}

export function isEmbeddableVideo(url) {
  const r = detectVideoEmbed(url);
  return r.provider === "youtube" || r.provider === "vimeo";
}
